import React, { useEffect, useState } from "react";
import InputField from "../generic/InputField";
import AlertBanner from "../generic/AlertBanner";
import MaterialTypeSelector from "../shared/MaterialTypeSelector";
import { MESSAGE_TYPES, MessageType } from "../../constants/Messages";

interface AdvancedFilterBarProps<T> {
    filters: {
        start_date?: string;
        end_date?: string;
        material_type?: number | null;
        color_code?: string | null;
        size?: number | null;
    };
    data: T[]; // Data to be filtered locally
    onChange: (filters: {
        start_date?: string;
        end_date?: string;
        material_type?: number | null;
        color_code?: string | null;
        size?: number | null;
    }) => void;
    onFilterData: (filteredData: T[]) => void; // Callback to pass filtered data
}

const AdvancedFilterBar = <T, >({ filters, data, onChange, onFilterData }: AdvancedFilterBarProps<T>) => {
    const [ localFilters, setLocalFilters ] = useState(filters);
    const [ alert, setAlert ] = useState<{ type: MessageType, message: string } | null>(null);
    const [ originalData, setOriginalData ] = useState<T[]>(data);

    // Update originalData when the `data` prop changes
    useEffect(() => {
        setOriginalData(data);
    }, [ data ]);

    // Handle date filter changes
    const handleFilterChange = (field: keyof typeof filters, value: string | undefined) => {
        const updatedFilters = { ...localFilters, [field]: value };

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize today's date to the start of the day

        // Validation: Start date must not be after today
        if (field === "start_date" && value) {
            const selectedDate = new Date(value);
            if (selectedDate > today) {
                setAlert({ type: MESSAGE_TYPES.ERROR, message: "Start date cannot be after today." });
                return;
            }
            if (updatedFilters.end_date && selectedDate > new Date(updatedFilters.end_date)) {
                setAlert({ type: MESSAGE_TYPES.ERROR, message: "Start date cannot be after the end date." });
                return;
            }
        }

        // Validation: End date must not be earlier than start date
        if (field === "end_date" && value && updatedFilters.start_date) {
            if (new Date(value) < new Date(updatedFilters.start_date)) {
                setAlert({ type: MESSAGE_TYPES.ERROR, message: "End date cannot be before the start date." });
                return;
            }
        }

        setAlert(null); // Clear any existing errors
        setLocalFilters(updatedFilters);
        onChange(updatedFilters);
    };


    // Handle material type, size, and color selection changes
    const handleMaterialTypeSelectorChange = (selection: {
        material_type_id: number | null;
        color_code: string | null;
        material_size_id: number | null;
        records: T[];
    }) => {
        const updatedFilters = {
            ...localFilters,
            material_type_id: selection.material_type_id,
            color_code: selection.color_code,
            material_size_id: selection.material_size_id,
        };
        setLocalFilters(updatedFilters);
        onChange(updatedFilters);

        // Apply local filtering based on the selection using originalData
        const filteredData = originalData.filter((item: any) => {
            const matchesMaterialType = selection.material_type_id === null || item.material_type_id === selection.material_type_id;
            const matchesColorCode = selection.color_code === null || item.color_code === selection.color_code;
            const matchesSize = selection.material_size_id === null || item.material_size_id === selection.material_size_id;

            return matchesMaterialType && matchesColorCode && matchesSize;
        });

        onFilterData(filteredData);
    };

    const handleCloseAlert = () => {
        setAlert(null);
    };

    return (
        <div className="flex flex-col rounded-lg w-full">
            {/* Alert Banner */}
            {alert && (
                <AlertBanner
                    message_type={MESSAGE_TYPES.ERROR}
                    message={alert.message}
                    onClose={handleCloseAlert}
                />
            )}
            {/* Start Date Input */}
            <InputField
                type="date"
                value={localFilters.start_date || ""}
                onChange={(value) => handleFilterChange("start_date", String(value || ""))}
                label="Start Date"
                id="start-time"
            />

            {/* End Date Input */}
            <InputField
                type="date"
                value={localFilters.end_date || ""}
                onChange={(value) => handleFilterChange("end_date", String(value || ""))}
                label="End Date"
                id="end-time"
            />

            {/* Material Type Selector */}
            <MaterialTypeSelector required={false} onSelect={handleMaterialTypeSelectorChange}/>
        </div>
    );
};

export default AdvancedFilterBar;
